import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, X, GripVertical } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_BASE_URL } from '../utils/apiBase';

const BACKEND_URL = API_BASE_URL;
const POS_STORAGE_KEY = 'backfill_chip_pos_v1';
const DISMISS_STORAGE_KEY = 'backfill_chip_dismissed_job';

/**
 * A DRAGGABLE floating progress pill that surfaces server-side backfill
 * progress from anywhere in the app. Drive-mode only.
 *
 * Why draggable? On mobile, a top-anchored toast/chip was blocking the
 * Settings gear in the header. Users can now grab the pill and drop it
 * anywhere on screen; the position is remembered in localStorage.
 *
 * Interaction rules
 *   • Tap (no drag) → opens Settings modal.
 *   • Drag handle (grip icon) → moves the pill.
 *   • × button → hides the pill for the current job only. Cleared when
 *     the job's id changes (i.e. a new backfill starts).
 *   • On viewport resize, position is clamped so the pill never sits
 *     off-screen after rotation or window-resize.
 *
 * The chip itself does not fire toasts or side-effects; it only reads
 * `/api/plants/backfill/status` every 6s while a job is active and
 * self-hides otherwise. SettingsModal remains the source of truth for
 * the completion summary.
 */
export const BackfillNotifierChip = ({ isLocalMode, onOpenSettings }) => {
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [pos, setPos] = useState(() => _readInitialPos());
  const [dismissedJobId, setDismissedJobId] = useState(() => {
    try { return localStorage.getItem(DISMISS_STORAGE_KEY) || null; } catch { return null; }
  });
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
  const chipRef = useRef(null);
  const intervalRef = useRef(null);

  // ── status polling ────────────────────────────────────────────────
  useEffect(() => {
    if (isLocalMode) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const sessionToken = localStorage.getItem('auth_hint');
        if (!sessionToken) return;
        const resp = await axios.get(
          `${BACKEND_URL}/api/plants/backfill/status`,
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 15000 }
        );
        if (cancelled) return;
        const j = resp.data?.job;
        if (j && j.status === 'running') {
          setJob(j);
        } else {
          setJob(null);
        }
      } catch (_err) {
        // silent — transient failures shouldn't spam the console
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 6000);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isLocalMode]);

  // ── clamp position on viewport resize ─────────────────────────────
  useEffect(() => {
    const clamp = () => setPos((p) => _clampToViewport(p, chipRef.current));
    window.addEventListener('resize', clamp);
    window.addEventListener('orientationchange', clamp);
    return () => {
      window.removeEventListener('resize', clamp);
      window.removeEventListener('orientationchange', clamp);
    };
  }, []);

  // Once the chip mounts, snap it to a valid on-screen position (initial
  // read used sensible defaults but couldn't measure width/height yet).
  useEffect(() => {
    if (chipRef.current) {
      setPos((p) => _clampToViewport(p, chipRef.current));
    }
    // Also runs when the chip first becomes visible (job appears).
  }, [job]);

  // ── clear dismissal when a new job starts ─────────────────────────
  useEffect(() => {
    if (!job) return;
    if (dismissedJobId && job.id && dismissedJobId !== job.id) {
      setDismissedJobId(null);
      try { localStorage.removeItem(DISMISS_STORAGE_KEY); } catch { /* ignore */ }
    }
  }, [job, dismissedJobId]);

  // ── drag handlers ─────────────────────────────────────────────────
  const onPointerDown = useCallback((e) => {
    // Ignore clicks on inner buttons (× and the pill itself for tap-to-open)
    if (e.target.closest('[data-chip-noop]')) return;
    // Only left mouse button, or touch/pen
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (!dragState.current.moved && Math.hypot(dx, dy) > 4) {
      dragState.current.moved = true;
    }
    if (dragState.current.moved) {
      e.preventDefault();
      const next = _clampToViewport(
        { x: dragState.current.origX + dx, y: dragState.current.origY + dy },
        chipRef.current
      );
      setPos(next);
    }
  }, []);

  const onPointerUp = useCallback((e) => {
    const wasMoved = dragState.current.moved;
    dragState.current.dragging = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (wasMoved) {
      // Persist final position
      try {
        localStorage.setItem(POS_STORAGE_KEY, JSON.stringify(pos));
      } catch { /* ignore */ }
    }
  }, [pos]);

  const handleTap = useCallback(() => {
    // Suppress tap that immediately followed a drag
    if (dragState.current.moved) return;
    if (onOpenSettings) onOpenSettings();
  }, [onOpenSettings]);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    const id = job?.id || 'unknown';
    setDismissedJobId(id);
    try { localStorage.setItem(DISMISS_STORAGE_KEY, id); } catch { /* ignore */ }
  }, [job]);

  // ── render guards ─────────────────────────────────────────────────
  if (!job) return null;
  if (dismissedJobId && job.id && dismissedJobId === job.id) return null;

  const filled = job.filled || 0;
  const total = job.total || 0;
  const label = t('backfillChipProgress')?.replace('{filled}', filled).replace('{total}', total)
    || `Filling ${filled}/${total}…`;
  const title = t('backfillChipTitle') || 'Backfill running — tap to open Settings';

  return (
    <div
      ref={chipRef}
      className="fixed z-[60] select-none touch-none"
      style={{
        left: `${pos.x}px`,
        top: `${pos.y}px`,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="backfill-notifier-chip-wrapper"
    >
      <div
        className="inline-flex items-center gap-1.5 pl-1 pr-1 py-1 rounded-full text-xs font-medium shadow-lg hover:shadow-xl transition-shadow cursor-grab active:cursor-grabbing"
        style={{
          backgroundColor: 'hsl(143, 45%, 34%)',
          color: 'white',
          border: '1px solid hsl(143, 45%, 28%)',
        }}
        role="status"
        aria-live="polite"
        aria-label={label}
        title={title}
      >
        {/* Drag handle — visually cues the pill is movable */}
        <GripVertical
          className="w-3.5 h-3.5 opacity-70"
          aria-hidden="true"
        />

        {/* Tap-to-open Settings — everything except the handle + × */}
        <button
          type="button"
          onClick={handleTap}
          data-chip-noop
          className="inline-flex items-center gap-1.5 px-1"
          style={{ background: 'transparent', color: 'inherit', border: 'none' }}
          data-testid="backfill-notifier-chip"
          aria-label={title}
        >
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
          <Sparkles className="w-3 h-3" aria-hidden="true" />
          <span className="whitespace-nowrap">{label}</span>
        </button>

        {/* Dismiss for this job only */}
        <button
          type="button"
          onClick={handleDismiss}
          data-chip-noop
          className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/15"
          style={{ background: 'transparent', color: 'inherit', border: 'none' }}
          aria-label={t('dismiss') || 'Hide'}
          title={t('dismiss') || 'Hide (backfill continues in background)'}
          data-testid="backfill-notifier-chip-dismiss"
        >
          <X className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

// ── helpers ─────────────────────────────────────────────────────────
function _readInitialPos() {
  try {
    const raw = localStorage.getItem(POS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  // Default: bottom-right on mobile, top-right on desktop. Both are
  // approximate — the real clamp runs after mount when we know the chip
  // size. Values chosen so it never overlaps the header (top-right icons)
  // or the mobile FAB area.
  if (typeof window !== 'undefined') {
    const vw = window.innerWidth || 375;
    const vh = window.innerHeight || 667;
    if (vw < 768) {
      // Mobile: bottom-right, above OS gesture bar
      return { x: Math.max(8, vw - 220), y: Math.max(8, vh - 120) };
    }
    // Desktop: top-right, below the sticky header
    return { x: Math.max(8, vw - 260), y: 88 };
  }
  return { x: 16, y: 88 };
}

function _clampToViewport(pos, node) {
  const vw = (typeof window !== 'undefined' && window.innerWidth) || 375;
  const vh = (typeof window !== 'undefined' && window.innerHeight) || 667;
  const w = node?.offsetWidth || 200;
  const h = node?.offsetHeight || 32;
  const margin = 4;
  return {
    x: Math.min(Math.max(margin, pos.x), Math.max(margin, vw - w - margin)),
    y: Math.min(Math.max(margin, pos.y), Math.max(margin, vh - h - margin)),
  };
}

export default BackfillNotifierChip;
